import { supabase } from "@/integrations/supabase/client";
import { 
  Breeder, 
  BreederBreed, 
  BreederPhoto, 
  AnimalListing, 
  AnimalMedia,
  BreederMessage,
  BreederReview,
  BreederFavorite
} from "@/types/breeder";
import {
  BreedersPublicSafeRow,
  BreederFilters,
  AnimalListingFilters,
  EnhancedBreederFilters
} from "@/types/supabase-views";
import { logger } from '@/utils/logger';

// Breeders
export const getBreedersList = async (filters?: BreederFilters) => {
  try {
    // Use the public safe view for listing - protects sensitive data
    // Individual breeder details are fetched with getBreederById which handles owner access
    let query = supabase.from('breeders_public_safe').select('*');
    
    if (filters) {
      if (filters.specialization) {
        query = query.contains('specializations', [filters.specialization]);
      }
      if (filters.region) {
        query = query.eq('region', filters.region);
      }
      if (filters.is_verified) {
        query = query.eq('is_verified', true);
      }
    }
    
    if (filters?.featured) {
      query = query.eq('is_featured', true);
    }
    
    const { data, error } = await query.order('is_premium', { ascending: false });
    
    if (error) throw error;
    
    // Map safe view data to Breeder type with safe defaults for missing fields
    const safeData = data as BreedersPublicSafeRow[] | null;
    return (safeData || []).map(item => ({
      ...item,
      // Fields from safe view use different names for contact availability
      show_phone: item.contact_phone_available ?? false,
      show_email: item.contact_email_available ?? false,
      // Fields not exposed in safe view
      phone: null,
      email: null,
      address: null,
      postal_code: null,
      siret: null,
      verification_docs: {},
      verification_status: 'approved' as const,
      is_active: true,
      total_contacts: 0,
      user_id: null,
      gallery_urls: null,
    })) as Breeder[];
  } catch (error) {
    logger.error('Error fetching breeders list:', error);
    throw error;
  }
};

export const getBreederById = async (id: string) => {
  try {
    // Check if user is authenticated to determine data access level
    const { data: { session } } = await supabase.auth.getSession();
    
    let query;
    if (session?.user) {
      // Authenticated user: access to full breeder data including contact info
      query = supabase
        .from('breeders')
        .select('*')
        .eq('id', id);
    } else {
      // Non-authenticated user: limited data via public safe view
      query = supabase
        .from('breeders_public_safe')
        .select('*')
        .eq('id', id);
    }
    
    const { data, error } = await query.single();
    
    if (error) throw error;
    return data as Breeder;
  } catch (error) {
    logger.error('Error fetching breeder by ID:', error);
    throw error;
  }
};

export const getBreederByUserId = async (userId: string) => {
  const { data, error } = await supabase
    .from('breeders')
    .select('*')
    .eq('user_id', userId)
    .single();
  
  if (error && error.code !== 'PGRST116') throw error; // PGRST116 = Not found
  return data as Breeder | null;
};

export const createBreeder = async (breederData: Omit<Breeder, 'id' | 'created_at' | 'updated_at'>) => {
  const { data: { session: _session2 }, error: userError } = await supabase.auth.getSession();
  const userData = { user: _session2?.user ?? null };
  
  if (userError) throw userError;
  
  const { data, error } = await supabase
    .from('breeders')
    .insert([{ ...breederData, user_id: userData.user.id }])
    .select()
    .single();
  
  if (error) throw error;
  return data as Breeder;
};

export const updateBreeder = async (id: string, breederData: Partial<Breeder>) => {
  const { data, error } = await supabase
    .from('breeders')
    .update(breederData)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data as Breeder;
};

export const deleteBreeder = async (userId: string) => {
  const { error } = await supabase
    .from('breeders')
    .delete()
    .eq('user_id', userId);
  
  if (error) throw error;
};

export const getBreederBreeds = async (breederId: string) => {
  const { data, error } = await supabase
    .from('breeder_breeds')
    .select('*')
    .eq('breeder_id', breederId);
  
  if (error) throw error;
  return data as BreederBreed[];
};

export const getBreederPhotos = async (breederId: string) => {
  const { data, error } = await supabase
    .from('breeder_photos')
    .select('*')
    .eq('breeder_id', breederId)
    .order('order_index');
  
  if (error) throw error;
  return data as BreederPhoto[];
};

// Animal Listings
export const getAnimalListings = async (filters?: AnimalListingFilters) => {
  let query = supabase.from('animal_listings').select('*');
  
  if (filters) {
    if (filters.species) {
      query = query.eq('species', filters.species);
    }
    if (filters.breed) {
      query = query.eq('breed', filters.breed);
    }
    if (filters.gender) {
      query = query.eq('gender', filters.gender);
    }
    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    if (filters.breeder_id) {
      query = query.eq('breeder_id', filters.breeder_id);
    }
    if (filters.is_adoption) {
      query = query.eq('is_adoption', true);
    }
  }
  
  const { data, error } = await query.order('created_at', { ascending: false });
  
  if (error) throw error;
  return data as AnimalListing[];
};

export const getAnimalById = async (id: string) => {
  const { data, error } = await supabase
    .from('animal_listings')
    .select('*')
    .eq('id', id)
    .single();
  
  if (error) throw error;
  return data as AnimalListing;
};

export const getAnimalMedia = async (animalId: string) => {
  const { data, error } = await supabase
    .from('animal_media')
    .select('*')
    .eq('animal_listing_id', animalId)
    .order('order_index');
  
  if (error) throw error;
  return data as AnimalMedia[];
};

// Messages
export const sendBreederMessage = async (message: Omit<BreederMessage, 'id' | 'is_read' | 'created_at'>) => {
  const { data, error } = await supabase
    .from('breeder_messages')
    .insert([message])
    .select()
    .single();
  
  if (error) throw error;
  return data as BreederMessage;
};

// Reviews - Only verified reviews
export const getBreederReviews = async (breederId: string) => {
  const { data, error } = await supabase
    .from('breeder_reviews')
    .select('*')
    .eq('breeder_id', breederId)
    .eq('is_verified', true)
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data as BreederReview[];
};

export const createBreederReview = async (review: {
  breeder_id: string;
  rating: number;
  comment?: string;
}) => {
  const { data: { session } } = await supabase.auth.getSession();
  const user = session?.user ?? null;
  if (!user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('breeder_reviews')
    .insert({
      breeder_id: review.breeder_id,
      user_id: user.id,
      rating: review.rating,
      comment: review.comment || null,
      is_verified: false
    })
    .select()
    .single();

  if (error) throw error;
  return data as BreederReview;
};

// Favorites
export const toggleBreederFavorite = async (userId: string, breederId: string) => {
  // Check if already favorited
  const { data: existingFav } = await supabase
    .from('breeder_favorites')
    .select('*')
    .eq('user_id', userId)
    .eq('breeder_id', breederId)
    .single();
  
  if (existingFav) {
    // Remove from favorites
    const { error } = await supabase
      .from('breeder_favorites')
      .delete()
      .eq('id', existingFav.id);
    
    if (error) throw error;
    return null;
  } else {
    // Add to favorites
    const { data, error } = await supabase
      .from('breeder_favorites')
      .insert([{ user_id: userId, breeder_id: breederId }])
      .select()
      .single();
    
    if (error) throw error;
    return data as BreederFavorite;
  }
};

export const getBreederFavorites = async (userId: string) => {
  const { data, error } = await supabase
    .from('breeder_favorites')
    .select('*, breeders(*)')
    .eq('user_id', userId);
  
  if (error) throw error;
  return data;
};

// Nouvelles fonctions pour les fonctionnalités améliorées
export const getBreederWithEnhancedData = async (id: string) => {
  try {
    // Récupérer les données de base de l'éleveur
    const breeder = await getBreederById(id);
    
    // Récupérer les données supplémentaires en parallèle
    const [badges, breedingAnimals, litters, reviews] = await Promise.all([
      supabase.from('breeder_badges').select('*').eq('breeder_id', id),
      supabase.from('breeding_animals').select('*').eq('breeder_id', id).eq('is_active', true),
      supabase.from('litters').select(`
        *,
        father:breeding_animals!litters_father_id_fkey(*),
        mother:breeding_animals!litters_mother_id_fkey(*)
      `).eq('breeder_id', id),
      supabase.from('breeder_reviews').select('*').eq('breeder_id', id)
    ]);

    return {
      ...breeder,
      badges: badges.data || [],
      breedingAnimals: breedingAnimals.data || [],
      litters: litters.data || [],
      reviews: reviews.data || [],
      averageRating: reviews.data?.length > 0 
        ? reviews.data.reduce((acc, review) => acc + review.rating, 0) / reviews.data.length 
        : 0,
      totalReviews: reviews.data?.length || 0
    };
  } catch (error) {
    logger.error('Error fetching enhanced breeder data:', error);
    throw error;
  }
};

export const searchBreedersWithEnhancedFilters = async (filters: EnhancedBreederFilters) => {
  try {
    // Always use the public safe view for search - protects sensitive data
    let query = supabase.from('breeders_public_safe').select('*');

    // Filtres existants
    if (filters.specialization) {
      query = query.contains('specializations', [filters.specialization]);
    }
    if (filters.region) {
      query = query.eq('region', filters.region);
    }
    if (filters.is_verified) {
      query = query.eq('is_verified', true);
    }

    const { data: breeders, error } = await query.order('is_premium', { ascending: false });
    
    if (error) throw error;

    // Traitement supplémentaire côté client pour les filtres avancés
    const safeData = breeders as BreedersPublicSafeRow[] | null;
    let filteredBreeders = safeData || [];

    if (filters.has_breeding_animals) {
      const breedersWithAnimals = await supabase
        .from('breeding_animals')
        .select('breeder_id')
        .eq('is_active', true);
      
      const breederIds = new Set(breedersWithAnimals.data?.map(b => b.breeder_id) || []);
      filteredBreeders = filteredBreeders.filter(b => breederIds.has(b.id));
    }

    if (filters.has_available_litters) {
      const breedersWithLitters = await supabase
        .from('litters')
        .select('breeder_id')
        .in('status', ['planned', 'born', 'ready']);
      
      const breederIds = new Set(breedersWithLitters.data?.map(l => l.breeder_id) || []);
      filteredBreeders = filteredBreeders.filter(b => breederIds.has(b.id));
    }

    // Map safe view data to Breeder type with safe defaults
    return filteredBreeders.map(item => ({
      ...item,
      show_phone: item.contact_phone_available ?? false,
      show_email: item.contact_email_available ?? false,
      phone: null,
      email: null,
      address: null,
      postal_code: null,
      siret: null,
      verification_docs: {},
      verification_status: 'approved' as const,
      is_active: true,
      total_contacts: 0,
      user_id: null,
      gallery_urls: null,
    })) as Breeder[];
  } catch (error) {
    logger.error('Error searching enhanced breeders:', error);
    throw error;
  }
};
